import React, { FC } from 'react';
import { BpIconTypes, IconName } from './types';
import { BpIcons } from './bpIcons';
import { baseTheme } from '../../setup/theme/impl/baseTheme';
import { styled } from '../../styles';
import { BpDesignSystem } from '../../types';
import { getTokenColor, TokenColor } from '../../setup/theme/impl/colors';
import { token } from '../../setup/theme/impl/getToken';

type IconSize = 'small' | 'medium' | 'large';
type IconStackPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

const isTest = process.env.NODE_ENV === 'test';

export type IconProps = BpDesignSystem.Props<
  {
    name: IconName;
    color?: 'inherit' | TokenColor | `var(${string})`;
    size?: number | IconSize;
    stack?: {
      name: IconName;
      position?: IconStackPosition;
      size?: 10 | 11 | 12 | 13 | 14 | 15 | 16;
      color?: 'inherit' | TokenColor | `var(${string})`;
      style?: React.CSSProperties;
    };
  } & Omit<React.HTMLProps<HTMLSpanElement>, 'size' | 'as'>
>;

const iconSizes: { [prop in IconSize]: string } = {
  small: token('sizeIconXxxs') ?? '16px',
  medium: token('sizeIconXs') ?? '24px',
  large: token('sizeIconMd') ?? '40px'
};

export const Icon: FC<IconProps> = ({
  name,
  className,
  color = baseTheme.tokens.colorOnSurfaceVariant,
  size = 'medium',
  dataTest,
  style,
  stack,
  ...spanProps
}) => {
  const iconSize = getSize(size);
  const fillColor = !color || color === 'inherit' ? 'currentColor' : getTokenColor(color);

  return (
    <StyledIconWrapper
      style={style}
      data-test={dataTest}
      data-icon-name={name}
      className={`material-symbols-rounded${className ? ` ${className}` : ''}`}
      $color={getTokenColor(color)}
      $fill={fillColor}
      $size={iconSize}
      {...spanProps}
    >
      {!isTest ? getIcon(name) : null}
      {!!stack && (
        <Icon
          name={stack.name}
          dataTest={`${dataTest ?? 'icon'}-stack`}
          color={stack.color ?? 'inherit'}
          size={stack.size ?? 12}
          style={{
            position: 'absolute',
            ...getPositionCoords(stack.position ?? 'bottom-right'),
            backgroundColor: '#fff',
            borderRadius: 2,
            ...stack.style
          }}
        />
      )}
    </StyledIconWrapper>
  );
};

const StyledIconWrapper = styled('span')<{ $color: string; $size: number | string; $fill: string }>(
  ({ $color, $fill, $size }) => {
    const targetSize = typeof $size === 'number' ? `${$size}px` : $size;
    return `
    display: inline-flex;
    position: relative;
    color: ${$color};
    fill: ${$color};
    font-size: ${targetSize};
    max-width: ${targetSize};
    
    & > svg {
      width: ${targetSize};
      height: ${targetSize};
      
      path {
        color: ${$color};
        fill: ${$fill};
      }
    }
  `;
  }
);

function getIcon(name: IconName): string | React.ReactNode {
  if (name in BpIcons) {
    return BpIcons[name as BpIconTypes];
  }
  return name.toLowerCase();
}

function getSize(size: number | IconSize): number | string {
  return typeof size === 'number' ? size : iconSizes[size];
}

function getPositionCoords(position: IconStackPosition): {
  left?: number;
  top?: number;
  bottom?: number;
  right?: number;
} {
  switch (position) {
    case 'top-left':
      return {
        left: 0,
        top: 0
      };
    case 'top-right':
      return {
        right: 0,
        top: 0
      };
    case 'bottom-left':
      return {
        left: 0,
        bottom: 0
      };
    case 'bottom-right':
      return {
        right: 0,
        bottom: 0
      };
  }
}
